import { useCallback, useState, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import MuiButton from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DeleteIcon from '@mui/icons-material/Delete';
import { FCX } from '@core/models';
import Button from '@components/Button';
import { useDeleteRespondents, useRestartDataGeneration } from '@core/api/useSurveys';
import { toast } from "react-toastify";
import Loader from '@components/Loader/Loader';
import IconButton from '@mui/material/IconButton';

const RemoveRespondentsPopup: FCX<{
    open: boolean;
    surveyName: string;
    surveyId: number;
    handleClosePopup: () => void;
    onOperationComplete?: () => void;
}> = ({
    open,
    surveyName,
    surveyId,
    handleClosePopup,
    onOperationComplete,
}) => {
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const {
        doFetch: deleteRespondents,
        isLoading: isDeletingRespondents,
    } = useDeleteRespondents();

    const {
        doFetch: restartDataGeneration,
        isLoading: isGeneratingData,
    } = useRestartDataGeneration()

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e?.target?.files?.[0]) {
            const file = (e.target.files ?? [])[0];
            if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
                setCsvFile(file);
            } else {
                toast.error('Please select a valid CSV file');
            }
        }
    }, []);

    const handleFileUpload = useCallback(() => {
        if (!csvFile) return;

        const formData = new FormData();
        formData.append('file', csvFile);
        formData.append('id', surveyId.toString());

        deleteRespondents({
            data: formData,
        })
            .then((res) => {
                if (res?.status === 200) {
                    toast.success('CSV Uploaded successfully!');
                    onOperationComplete?.();
                    handleClosePopup();
                } else {
                    toast.error('Failed to delete respondents');
                }
            })
    }, [csvFile, surveyId, deleteRespondents, handleClosePopup, onOperationComplete]);

    const handleDataGeneration = useCallback(() => {
        restartDataGeneration({
            params: { id: surveyId },
        })
            .then((res) => {
                if (res?.status === 200) {
                    toast.success('Regenerated Data successfully!');
                    onOperationComplete?.();
                    handleClosePopup();
                } else {
                    toast.error('Failed to regenerate data');
                }
            })
    }, [surveyId, restartDataGeneration, handleClosePopup, onOperationComplete]);

    const handleFileRemove = useCallback(() => {
        setCsvFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const handleFileSelectClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleClose = useCallback(() => {
        handleFileRemove()
        handleClosePopup();
    }, [handleFileRemove, handleClosePopup]);

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>
                <div>Survey Details</div>
                <div className="SurveyDetails__sub-header">Name: {surveyName}</div>
                <div className="SurveyDetails__sub-header">Survey ID: {surveyId}</div>
            </DialogTitle>
            <DialogContent dividers className="SurveyDetails__respondent-popup-content">
                {(isDeletingRespondents || isGeneratingData) && <Loader />}
                <div className="SurveyDetails__upload-csv">
                    <Button
                        className="SurveyDetails__popup-button"
                        onClick={csvFile ? handleFileUpload : handleFileSelectClick} 
                        isDisabled={isDeletingRespondents}
                    >
                        <span>{csvFile ? 'Upload' : 'Select CSV File'}</span>
                        <input
                            className="SurveyDetails__hidden"
                            ref={fileInputRef}
                            type="file"
                            accept="text/csv,.csv"
                            onChange={handleFileSelect}
                        />
                    </Button>
                    
                    {csvFile ? (
                        <div className="SurveyDetails__sub-text-container">
                            <div
                                className="SurveyDetails__selected-file-text"
                                title={csvFile.name}
                            >
                                <span>{csvFile.name}</span>
                            </div>
                            <IconButton onClick={handleFileRemove}>
                                <DeleteIcon />
                            </IconButton>
                        </div>
                    ) : (
                        <div className="SurveyDetails__sub-text-container">
                            <span>File should be in csv format and contain only the list of respondent ID&apos;s selected for removal</span>
                        </div>
                    )}
                </div>
                <div className="SurveyDetails__restart-generation">
                    <Button
                        className="SurveyDetails__popup-button"
                        onClick={handleDataGeneration}
                        isDisabled={isGeneratingData}
                    >
                        Restart Data Generation
                    </Button>
                    {
                        isGeneratingData && (
                            <div className="SurveyDetails__sub-text-container">
                                <span>Generation in progress!</span>
                            </div>
                        )
                    }
                </div>
            </DialogContent>
            <DialogActions>
                <MuiButton color="inherit" onClick={handleClose}>
                    Cancel
                </MuiButton>
            </DialogActions>
        </Dialog>
    );
};

export default RemoveRespondentsPopup;
